import { CSSProperties, useEffect, useRef } from "react";
import { MediaPlayer } from "dashjs";

type VideoPlayerProps = {
    src: string;
    licenseServerUrl?: string;
    preferredKeySystems?: string;
    poster?: string;
    autoPlay?: boolean;
    controls?: boolean;
    className?: string;
    width?: CSSProperties["width"];
    style?: CSSProperties;
};

const isMpdSource = (src: string) => /\.mpd(?:$|[?#])/i.test(src);

export const VideoPlayer = ({
    src,
    licenseServerUrl,
    preferredKeySystems,
    poster,
    autoPlay = false,
    controls = true,
    className,
    width,
    style,
}: VideoPlayerProps) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);

    useEffect(() => {
        const video = videoRef.current;
        if (!video || !src || !isMpdSource(src)) {
            return;
        }

        const player = MediaPlayer().create();

        const resolvedKeySystem =
            preferredKeySystems ?? process.env.REACT_APP_DRM_KEY_SYSTEM;
        const resolvedLicenseServerUrl =
            licenseServerUrl ?? process.env.REACT_APP_DRM_LICENSE_SERVER_URL;

        if (resolvedKeySystem && resolvedLicenseServerUrl) {
            player.setProtectionData({
                [resolvedKeySystem]: {
                    serverURL: resolvedLicenseServerUrl,
                },
            });
        }

        player.initialize(video, src, autoPlay);

        return () => {
            player.reset();
        };
    }, [src, autoPlay, preferredKeySystems, licenseServerUrl]);

    return (
        <video
            ref={videoRef}
            src={isMpdSource(src) ? undefined : src}
            poster={poster}
            controls={controls}
            autoPlay={autoPlay}
            className={className}
            style={{
                width: width ?? "100%",
                ...(width === undefined ? { maxWidth: 960 } : {}),
                ...style,
            }}
        >
            お使いのブラウザは動画再生に対応していません。
        </video>
    );
};